import { Component, inject, OnInit } from '@angular/core';
import {
  BloodTypeOptions,
  CustomerFullInformation,
  MaritalStatusOptions,
  SexOptions
} from '../../models/customer-information.model';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputNumber } from 'primeng/inputnumber';
import { Textarea } from 'primeng/textarea';
import { Button } from 'primeng/button';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PreventDoubleClickDirective } from '../../../../shared/directives/prevent-double-click.directive';
import { Mode } from '../../../../shared/models/mode.model';
import { Router } from '@angular/router';
import { CustomerInformationStore } from '../../stores/customer-information.store';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { dataURLtoFile } from '../../../../shared/utils/utils';
import { CustomerInformationService } from '../../services/customer-information.service';
import { DatePicker } from 'primeng/datepicker';
import { SelectComponent } from '../../../../shared/components/select/select.component';
import { DatePipe, NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { InputText } from 'primeng/inputtext';
import { Divider } from 'primeng/divider';
import { SpinnerStateService } from '../../../../shared/services/spinner-state.service';
import { DialogAlertComponent } from '../../../../shared/components/dialog-alert/dialog-alert.component';
import { TableModule } from 'primeng/table';
import { OPDService } from '../../../queue/services/opd.service';
import { CheckOPDList, OPDStatus } from '../../../queue/models/check-opd.model';
import { Tag } from 'primeng/tag';
import dayjs from 'dayjs';
import { BaseComponent } from '../../../../shared/components/base/base.component';
import { LoadingComponent } from '../../../../shared/components/loading/loading.component';
import { Paginator } from 'primeng/paginator';
import DialogMakeOpdComponent from '../../../queue/components/dialog-make-opd/dialog-make-opd.component';

@Component({
  selector: 'app-dialog-create-customer-information',
  imports: [
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    ReactiveFormsModule,
    InputNumber,
    Textarea,
    Button,
    PreventDoubleClickDirective,
    NgxDropzoneModule,
    DatePicker,
    SelectComponent,
    NgClass,
    TranslatePipe,
    DatePipe,
    InputText,
    Divider,
    TableModule,
    Tag,
    LoadingComponent,
    Paginator
  ],
  templateUrl: './dialog-create-customer-information.component.html',
  styleUrl: './dialog-create-customer-information.component.scss'
})
export class DialogCreateCustomerInformationComponent extends BaseComponent implements OnInit {
  form: FormGroup;
  formHealth: FormGroup;
  mode: Mode;
  title: string;
  titleParams: Record<string, unknown> = {};
  isSubmit: boolean;
  isSaving: boolean;
  showFooter = true;
  activeTab = '0';
  loading: boolean;
  checkOPDList: CheckOPDList;
  selectedFileBase64: any;
  selectedFiles: File[] = [];
  sexOptions = SexOptions;
  maritalStatusOptions = MaritalStatusOptions;
  bloodTypeOptions = BloodTypeOptions;
  Mode = Mode;
  fb = inject(FormBuilder);
  dialogService = inject(DialogService);
  dialogRef = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  router = inject(Router);
  customerStore = inject(CustomerInformationStore);
  customerInformationService = inject(CustomerInformationService);
  opdService = inject(OPDService);
  spinnerState = inject(SpinnerStateService);
  private originalData: CustomerFullInformation;

  get isEditing(): boolean {
    return this.mode === Mode.EDIT || this.mode === Mode.CREATE;
  }

  ngOnInit(): void {
    if (this.config.data?.id) {
      this.mode = Mode.VIEW;
      this.customerInformationService.getCustomerById(this.config.data.id).subscribe({
        next: res => {
          this.createForm(res.data);
          this.formHealth.disable();
          if (this.config.data?.action === 'makeOPD') {
            this.goToOPD();
          }
          this.title = 'page.customer.title.view';
          this.titleParams = {
            name: `${res.data.name}${res.data.surname ? ' ' + res.data.surname : ''}`,
            id: res.data.id
          };
          this.originalData = res.data;
          if (res.data.photo) {
            this.selectedFiles = [dataURLtoFile(res.data.photo, '')];
            this.selectedFileBase64 = res.data.photo;
          }
        },
        error: () => {
          this.onClose();
        }
      });
    } else {
      this.mode = Mode.CREATE;
      this.title = 'page.customer.title.create';
      this.createForm();
    }
  }

  createForm(d?: CustomerFullInformation): void {
    this.form = this.fb.group({
      id: [d?.id ?? null],
      photo: [d?.photo ?? null],
      prefix: [d?.prefix ?? null],
      sex: [d?.sex ?? null],
      nationality: [d?.nationality ?? null],
      religion: [d?.religion ?? null],
      maritalStatus: [d?.maritalStatus ?? null],
      bloodType: [d?.bloodType ?? null],
      birthday: [d?.birthday ? new Date(d.birthday) : null],
      idCardNumber: [d?.idCardNumber ?? null],
      passport: [d?.passport ?? null],
      name: [d?.name ?? null, Validators.required],
      surname: [d?.surname ?? null],
      nickname: [d?.nickname ?? null],
      nameEN: [d?.nameEN ?? null],
      surnameEN: [d?.surnameEN ?? null],
      tel1: [d?.tel1 ?? null],
      tel2: [d?.tel2 ?? null],
      address: [d?.address ?? null],
      subDistrict: [d?.subDistrict ?? null],
      district: [d?.district ?? null],
      province: [d?.province ?? null],
      zipCode: [d?.zipCode ?? null],
      note: [d?.note ?? null]
    });

    this.formHealth = this.fb.group({
      weight: [d?.weight ?? 0],
      height: [d?.height ?? 0],
      drugAllergy: [d?.drugAllergy],
      mentalHealth: [d?.mentalHealth],
      congenitalDisease: [d?.congenitalDisease],
      healthNote: [d?.healthNote]
    });
  }

  onEdit(): void {
    this.mode = Mode.EDIT;
    this.formHealth.enable();
  }

  goToOPD(): void {
    this.customerStore.setCustomerFullInformation({ ...this.form.getRawValue(), ...this.formHealth.getRawValue() });
    this.router.navigate(['/queue/check-opd']);
    this.dialogRef.close();
  }

  goToView(docNo: string): void {
    this.dialogService.open(DialogMakeOpdComponent, {
      styleClass: 'p-dialog-maximized p-dialog-content-footer',
      showHeader: false,
      data: {
        docNo: docNo
      }
    });
  }

  onSave(): void {
    this.isSubmit = true;

    if (this.form.invalid) {
      return;
    }
    this.isSaving = true;

    this.form.patchValue({ photo: this.selectedFileBase64 });

    this.customerInformationService
      .saveCustomerInformation({
        ...this.form.getRawValue(),
        ...this.formHealth.getRawValue()
      })
      .subscribe({
        next: res => {
          this.isSaving = false;
          this.dialogService.open(DialogAlertComponent, {
            showHeader: false,
            data: {
              gif: 'assets/images/check.gif',
              header: 'common.message.SAVED_SUCCESSFULLY',
              close: 'common.button.ok'
            },
            width: '320px'
          });
          this.mode = Mode.VIEW;
          this.originalData = res.data;
          this.createForm(res.data);
          this.formHealth.disable();
          this.config.data = {
            id: res.data.id
          };
          this.title = 'page.customer.title.view';
          this.titleParams = {
            name: `${res.data.name}${res.data.surname ? ' ' + res.data.surname : ''}`,
            id: res.data.id
          };
          this.form.patchValue({ photo: this.selectedFileBase64 });
        },
        error: err => {
          this.isSaving = false;
          if (err.error.code === 'DUPLICATE_ID_CARD_NUMBER') {
            this.dialogService.open(DialogAlertComponent, {
              showHeader: false,
              data: {
                gif: 'assets/images/error.gif',
                header: 'common.message.ID_CARD_NUMBER_ALREADY_EXISTS',
                close: 'common.button.ok'
              },
              width: '320px'
            });
          }
        }
      });
  }

  onCancel(): void {
    if (this.mode === Mode.EDIT) {
      this.mode = Mode.VIEW;
      this.createForm(this.originalData);
      this.formHealth.disable();
      if (this.originalData?.photo) {
        this.selectedFiles = [dataURLtoFile(this.originalData.photo, '')];
        this.selectedFileBase64 = this.originalData.photo;
      } else {
        this.selectedFiles = [];
        this.selectedFileBase64 = null;
      }
    } else {
      this.onClose();
    }
  }

  onSelect(event: any) {
    this.onRemove();
    const input = event.addedFiles;
    if (input && input.length > 0 && input[0] instanceof File) {
      const reader = new FileReader();
      reader.onload = () => {
        this.selectedFileBase64 = reader.result;
      };
      reader.readAsDataURL(input[0]);
      this.selectedFiles.push(input[0]);
    }
  }

  onReadCard() {
    this.customerInformationService.readCardID().subscribe({
      next: res => {
        const data = res.data;

        const photo = data.photo ? `data:image/jpeg;base64,${data.photo}` : null;

        let birthday: Date | null = null;
        if (data.dateOfBirth) {
          const [year, month, day] = data.dateOfBirth.split('-');
          birthday = new Date(`${+year - 543}-${month}-${day}`);
        }

        const genderMap: Record<string, string> = { male: 'M', female: 'F' };

        this.form.patchValue({
          photo,
          prefix: data.titleTh ?? null,
          sex: genderMap[data.gender] ?? null,
          birthday,
          idCardNumber: data.citizenId ?? null,
          name: data.firstNameTh ?? null,
          surname: data.lastNameTh ?? null,
          nameEN: data.firstNameEn ?? null,
          surnameEN: data.lastNameEn ?? null,
          address: data.address ?? null
        });

        if (photo) {
          this.selectedFileBase64 = photo;
          this.selectedFiles = [dataURLtoFile(photo, 'photo.jpg')];
        }
      },
      error: error => {
        if (error.status !== 0) {
          this.dialogService.open(DialogAlertComponent, {
            showHeader: false,
            data: {
              gif: 'assets/images/error.gif',
              header: error.error.code ? `common.message.${error.error.code}` : 'common.message.SOMETHING_WENT_WRONG',
              close: 'common.button.ok'
            },
            width: '320px'
          });
        }
      }
    });
  }

  onRemove() {
    this.selectedFileBase64 = null;
    this.selectedFiles = [];
    this.form.patchValue({ photo: null });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onTab(index: number): void {
    this.activeTab = index.toString();
    this.showFooter = [0, 1].includes(index);

    if (index === 2) {
      this.fetchData();
    }
  }

  fetchData(e?: any) {
    const param = {
      page: e ? e.page + 1 : 1,
      pageSize: e ? e.rows : 20,
      createdDate: this.form.get('createdDate')?.value
        ? dayjs(this.form.get('createdDate')?.value).format('YYYY-MM-DD')
        : '',
      customerId: this.config.data.id,
      sort: 'created',
      sortBy: 'desc'
    };
    if (e) {
      this.first = e.first ?? 0;
    }

    this.loading = true;
    this.opdService.getCheckOPD(param).subscribe({
      next: res => {
        this.checkOPDList = res.data;
        this.first = e?.first ?? 0;
        this.loading = false;
      },
      error: () => {
        this.checkOPDList = {
          content: []
        } as any;
        this.loading = false;
      }
    });
  }

  calOPDStatus(status: OPDStatus) {
    switch (status) {
      case OPDStatus.WAITING_FOR_EXAMINING:
        return 'warn';
      case OPDStatus.EXAMINING:
        return 'info';
      case OPDStatus.WAITING_FOR_PAYMENT:
        return 'danger';
      case OPDStatus.COMPLETE:
        return 'success';
      case OPDStatus.CANCEL:
        return 'secondary';
    }
  }
}
